import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * PivotDropZone — bordered drop target with a label, used for the four
 * Builder slots: Rows, Columns, Values, Filters.
 *
 * Headless w.r.t. drag-and-drop (no @dnd-kit dep): the consuming page
 * passes `isOver` / `error` flags from outside (typically derived from
 * `useDroppable({ id: 'rows' }).isOver`). Children render however the
 * page wants — typically a wrap of <ColumnChip /> instances.
 *
 * States:
 *   default  — neutral border, label muted
 *   filled   — same neutral border, but content present
 *   isOver   — primary-colored dashed border + tinted background ("drop here")
 *   error    — error-colored border + small message slot
 *   disabled — muted, no hover
 */

const zoneVariants = cva('min-h-[48px] rounded-md border-2 border-dashed p-2 transition-colors', {
  variants: {
    state: {
      empty: 'border-ia-border bg-card',
      filled: 'border-ia-border bg-card',
      isOver: 'border-primary bg-primary/5',
      error: 'border-ia-error bg-ia-error-bg',
      disabled: 'border-ia-border bg-ia-elevated opacity-60',
    },
  },
  defaultVariants: { state: 'empty' },
})

export interface PivotDropZoneProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title' | 'children'>,
    VariantProps<typeof zoneVariants> {
  /** Label rendered above the drop area (e.g., "ROWS", "VALUES"). */
  label: string
  /** Hint shown when the zone is empty (e.g., "Drag dimension columns here"). */
  emptyHint?: string
  /** Visual state controlled by the consuming page. */
  isOver?: boolean
  /** Error message to display + flips zone to error styling. */
  error?: string
  /** When true, applies the disabled visual + ignores hover effects. */
  disabled?: boolean
  /** Chips (or any nodes) that fill the drop area. */
  children?: React.ReactNode
  /** Optional element after the label (e.g., a count badge). */
  meta?: React.ReactNode
}

export const PivotDropZone = React.forwardRef<HTMLDivElement, PivotDropZoneProps>(
  ({ className, label, emptyHint, isOver, error, disabled, children, meta, ...props }, ref) => {
    const hasChildren = React.Children.count(children) > 0
    const state = disabled
      ? 'disabled'
      : error
        ? 'error'
        : isOver
          ? 'isOver'
          : hasChildren
            ? 'filled'
            : 'empty'

    return (
      <div className={cn('flex flex-col gap-1', className)}>
        <div className="flex items-center justify-between px-1">
          <span className="text-caption font-semibold uppercase tracking-wide text-ia-text-secondary">
            {label}
          </span>
          {meta && <span className="text-caption text-ia-text-tertiary">{meta}</span>}
        </div>
        <div ref={ref} className={zoneVariants({ state })} {...props}>
          {hasChildren ? (
            <div className="flex flex-wrap gap-1">{children}</div>
          ) : (
            <div className="flex items-center justify-center text-caption text-ia-text-tertiary py-2">
              {emptyHint ?? 'Drop columns here'}
            </div>
          )}
        </div>
        {error && (
          <div className="text-caption text-ia-error px-1" role="alert">
            {error}
          </div>
        )}
      </div>
    )
  },
)
PivotDropZone.displayName = 'PivotDropZone'
